<template>
  <div class="w-full flex items-center justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 shrink-0 p-4">
      <div class="flex items-start justify-between gap-3 h-10 shrink-0">
        <div class="flex items-start gap-0.5 text-text-glass-default">
          <Icon icon="Function-FiII-Description" class="w-4 h-4 mt-0.75" />
          <span class="text-s-medium">{{ $t('keys.dks.intro') }}</span>
        </div>
        <Button
          color="glass"
          class="bg-white text-black w-12"
          size="xs"
          :disabled="!hasAnyKey"
          @click="handleTakeEffect"
        >
          {{ $t('keys.dks.apply') }}
        </Button>
      </div>
      <div class="flex-1">
        <USlider
          ref="sliderRef"
          :node-keys="nodeKeys"
          :active-node-index="activeNodeIndex"
          @node-click="handleNodeClick"
          @node-drop="handleNodeDrop"
          @node-remove="handleNodeRemove"
          @covered-change="handleCoveredChange"
        />
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :key-class="getKeyClass"
          :key-disabled="getKeyDisabled"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, inject, watch, watchEffect, nextTick } from 'vue'
  import { Keyboard, Icon, Button, toast } from '@rapoo/element'
  import USlider from './components/USlider.vue'
  import { fullSize108 } from '@/utils/fullSize108'
  import { setDragData, DragType } from '@/utils/dnd'
  import { useLocaleStore } from '@/stores'
  import { SelectedKeySymbol, KeySettingsSymbol, UpdateSelectedKeyMappingSymbol } from '../../types'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    getKeyConfigByTypeCode,
  } from '../../utils/keyMapSettings'
  import { findSettingByTargetKey, parseNumber } from '../../utils/keyIdentity'

  import type { DKS } from '@rapoo/device-sdk'
  import type {
    KeyboardConfig,
    KeyEventName,
    KeyboardKeyEventPayload,
    KeyConfig,
  } from '@rapoo/element'
  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../../types'

  const { t } = useLocaleStore()

  type KeyData = { keywords: string }

  /** 单键在 keycode 元数据中的 type */
  const SINGLE_KEY_META_TYPE = 0x01
  /** 与 USlider 保持一致的节点锚点，用于回显拖拽进度 */
  const NODE_ANCHORS = [0, 0.31, 0.69, 1] as const

  const selectedKey = inject(SelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const MAX_KEYS = 4
  const NODE_COUNT = 4

  const sliderRef = ref<InstanceType<typeof USlider> | null>(null)

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart']

  // 每个节点独立存储按键，最多4个节点，每个节点最多4个按键
  const nodeKeys = ref<KeyData[][]>(Array.from({ length: NODE_COUNT }, () => []))

  // 当前激活的节点索引，默认第一个
  const activeNodeIndex = ref<number>(0)

  /** 被前方拖拽范围覆盖的节点，不可填写/拖拽 */
  const coveredNodeIndexes = ref<number[]>([])

  const isNodeCovered = (index: number) => coveredNodeIndexes.value.includes(index)

  // 所有节点中已选中的按键（去重，用于判断总数和高亮）
  const highlightedKeyCodes = computed(() => new Set(nodeKeys.value.flat().map((k) => k.keywords)))

  const hasAnyKey = computed(() => highlightedKeyCodes.value.size > 0)

  const handleCoveredChange = (indexes: number[]) => {
    coveredNodeIndexes.value = indexes
    // 当前激活节点被覆盖时，切到第一个可用节点
    if (isNodeCovered(activeNodeIndex.value)) {
      const next = Array.from({ length: NODE_COUNT }, (_, i) => i).find((i) => !isNodeCovered(i))
      activeNodeIndex.value = next ?? 0
    }
  }

  /**
   * 从 DKS raw 回填节点按键与滑块拖拽进度。
   *
   * triggerBits 每位键码一个字节，每节点 2bit：
   * - 0b11：该节点点触发（键在此节点，未拖拽）
   * - 0b01：按下起点（键在此节点，已拖拽）
   * - 0b10：释放终点（仅编码，键不放在此节点）
   */
  const restoreFromDks = async (dks: DKS) => {
    const restored: KeyData[][] = Array.from({ length: NODE_COUNT }, () => [])
    const progressValues: number[] = [...NODE_ANCHORS]
    const keyCodes = dks.keyCodes ?? []
    const triggerBits = dks.triggerBits ?? []

    for (let i = 0; i < keyCodes.length; i++) {
      const code = keyCodes[i]
      if (!code) continue

      const config = getKeyConfigByTypeCode(SINGLE_KEY_META_TYPE, code)
      if (!config?.keywords) continue

      const byte = triggerBits[i] ?? 0
      let pressNode: number | null = null
      let releaseNode: number | null = null

      for (let nodeIdx = 0; nodeIdx < NODE_COUNT; nodeIdx++) {
        const bits = (byte >> (nodeIdx * 2)) & 0b11
        if (bits === 0b11 || bits === 0b01) {
          pressNode = nodeIdx
          restored[nodeIdx].push({ keywords: config.keywords })
        }
        if (bits === 0b10) {
          releaseNode = nodeIdx
        }
      }

      // 已拖拽：起点 0b01 + 终点 0b10 → 回显手柄吸附到终点附近
      if (
        pressNode != null &&
        releaseNode != null &&
        pressNode < NODE_COUNT - 1 &&
        ((byte >> (pressNode * 2)) & 0b11) === 0b01
      ) {
        progressValues[pressNode] = NODE_ANCHORS[releaseNode] - 0.16
      }
    }

    nodeKeys.value = restored
    activeNodeIndex.value = 0

    await nextTick()
    sliderRef.value?.setProgress?.(progressValues)
  }

  // 选中已是 DKS 的按键时回显；取消选中/切键不清空，离开 tab 卸载后自然清空
  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key || !keySettings?.value) return

      const setting = findSettingByTargetKey(keySettings.value, key)
      if (setting?.keyType !== 'DKS' || !setting.raw?.dks) return

      void restoreFromDks(setting.raw.dks as DKS)
    },
    { immediate: true },
  )

  // 监听虚拟键盘点击选中后，如果功能框已填，则唤出toast
  watchEffect(() => {
    if (selectedKey && selectedKey.value && hasAnyKey.value) {
      toast.info(t('toast.keys.dksApply'))
    }
  })

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    const isCybercafe = (import.meta.env.VITE_CHANNEL as string) === 'china_cybercafe'

    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row
          // 网吧版常规键盘去掉 Fn 源键
          .filter((key) => !(isCybercafe && key.keywords === 'Fn'))
          .map((key) => {
            const { w, h, x, y, keywords, ...rest } = key
            const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

            return {
              ...rest,
              ...config,
              rect: {
                w,
                h,
                x,
                y,
              },
            }
          }),
      ),
    }
  })

  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      if (isNodeCovered(activeNodeIndex.value)) return

      const keywords = data.keywords
      const activeNode = nodeKeys.value[activeNodeIndex.value]
      // 当前激活节点已有该键 → 忽略
      if (activeNode.some((k) => k.keywords === keywords)) return
      // 总数已满且该键是新键 → 忽略
      if (highlightedKeyCodes.value.size >= MAX_KEYS && !highlightedKeyCodes.value.has(keywords))
        return
      activeNode.push(data)
    }

    if (eventName === 'dragstart') {
      const dragEvent = nativeEvent as DragEvent
      if (!dragEvent.dataTransfer) return
      const keyData = data
      setDragData(dragEvent.dataTransfer, DragType.KEY_MAPPING, keyData as any)
    }
  }

  const handleNodeClick = (index: number) => {
    if (isNodeCovered(index)) return
    activeNodeIndex.value = index
  }

  const handleNodeDrop = (payload: { nodeIndex: number; keyData: KeyData }) => {
    const { nodeIndex, keyData } = payload
    if (isNodeCovered(nodeIndex)) return

    const node = nodeKeys.value[nodeIndex]
    // 该节点已有该键 → 忽略
    if (node.some((k) => k.keywords === keyData.keywords)) return
    // 总数已满且该键是新键 → 忽略
    if (
      highlightedKeyCodes.value.size >= MAX_KEYS &&
      !highlightedKeyCodes.value.has(keyData.keywords)
    )
      return
    node.push(keyData)
    activeNodeIndex.value = nodeIndex
  }

  const handleNodeRemove = (payload: { nodeIndex: number; keyIndex: number }) => {
    nodeKeys.value[payload.nodeIndex].splice(payload.keyIndex, 1)
  }

  /**
   * 根据 nodeKeys 和 USlider 拖拽状态构建 DKS triggerBits。
   *
   * 每个 HID keyCode 对应一个字节，2bit/行程位置：
   * [release2(2bit)][release1(2bit)][press2(2bit)][press1(2bit)]
   *
   * 未拖拽（progress ≈ anchor）：该节点为点触发 0b11（按下+释放）
   * 已拖拽（progress 偏离 anchor 到达下一节点附近）：起始节点 0b01（按下），到达节点 0b10（释放）
   */
  const buildTriggerBits = (allKeyCodes: number[]): number[] => {
    const slider = sliderRef.value
    const progressValues = slider?.progress ?? []
    const anchors: readonly number[] = slider?.NODE_ANCHORS ?? NODE_ANCHORS

    /**
     * 判断拖拽手柄 handleIdx 是否被拖到了目标节点 targetNodeIdx 附近。
     * 手柄吸附到目标节点时 progress ≈ anchor[targetNodeIdx] - 0.16
     */
    const getDragTargetNode = (handleIdx: number): number | null => {
      const currentP = progressValues[handleIdx] ?? anchors[handleIdx]
      const anchorP = anchors[handleIdx]

      // 未拖拽：progress 与 anchor 基本一致
      if (Math.abs(currentP - anchorP) < 0.02) return null

      // 已拖拽：找到 progress 到达了哪个下一节点
      for (let j = handleIdx + 1; j < anchors.length; j++) {
        const snapTarget = anchors[j] - 0.16
        if (Math.abs(currentP - snapTarget) < 0.02) return j
      }

      // 拖拽中但未吸附到任何节点，找最近的下一节点
      for (let j = handleIdx + 1; j < anchors.length; j++) {
        if (currentP < anchors[j]) return j
      }

      return null
    }

    return allKeyCodes.map((hidCode) => {
      let byte = 0

      for (let nodeIdx = 0; nodeIdx < NODE_COUNT; nodeIdx++) {
        const nodeHasKey = nodeKeys.value[nodeIdx].some((k) => {
          const key = KEYCODE_KEYWORDS_MAP.get(k.keywords)

          if (key) {
            return parseNumber(key.code) === hidCode
          }

          return false
        })
        if (!nodeHasKey) continue

        // 只有前3个节点有拖拽手柄（handle 0, 1, 2），最后一个节点（index 3）没有手柄
        if (nodeIdx < NODE_COUNT - 1) {
          const dragTarget = getDragTargetNode(nodeIdx)

          if (dragTarget === null) {
            // 未拖拽：点触发，按下+释放
            byte |= 0b11 << (nodeIdx * 2)
          } else {
            // 已拖拽：起始节点仅按下，目标节点仅释放
            byte |= 0b01 << (nodeIdx * 2)
            byte |= 0b10 << (dragTarget * 2)
          }
        } else {
          // 最后一个节点没有手柄，始终为点触发
          byte |= 0b11 << (nodeIdx * 2)
        }
      }

      return byte
    })
  }

  const handleTakeEffect = () => {
    if (!hasAnyKey.value) {
      toast.info(t('toast.keys.dksSelectKeyFirst'))
      return
    }

    if (!selectedKey?.value) {
      toast.info(t('toast.keys.dksSelect'))
      return
    }

    if (!updateSelectedKeyMapping) return

    // 收集所有去重的 HID keyCodes，最多4个
    const allKeys = nodeKeys.value.flat()
    const uniqueKeyMap = new Map<string, KeyConfig | undefined>()
    for (const k of allKeys) {
      const keyConfig = KEYCODE_KEYWORDS_MAP.get(k.keywords)
      if (!uniqueKeyMap.has(k.keywords)) {
        uniqueKeyMap.set(k.keywords, keyConfig)
      }
    }

    const uniqueKeys = Array.from(uniqueKeyMap.entries())
    // 填充到4个位置，不足的补0
    const keyCodes: number[] = []
    const keyLabels: string[] = []
    for (let i = 0; i < MAX_KEYS; i++) {
      if (i < uniqueKeys.length) {
        const keyConfig = uniqueKeyMap.get(uniqueKeys[i][0])
        keyCodes.push(parseNumber(keyConfig?.code || 0)!)
        keyLabels.push(getLocalizedField(keyConfig?.alias || keyConfig?.name))
      } else {
        keyCodes.push(0)
      }
    }

    const triggerBits = buildTriggerBits(keyCodes)

    // 行程位置固定值 (单位: 0.01mm)
    const dks: DKS = {
      actPoint1: 20, // 0.2mm
      actPoint2: 330, // 3.3mm
      releasePoint1: 330, // 3.3mm
      releasePoint2: 20, // 0.2mm
      triggerBits,
      keyCodes,
    }

    const dksKeyLabels = keyLabels.filter(Boolean).join('/')

    const payload: KeyChangePayload = {
      keywords: selectedKey.value.keywords,
      keyType: 'DKS',
      target: null,
      raw: {
        type: 0x0c,
        dks,
        dksKeyLabels,
      },
    }

    updateSelectedKeyMapping(payload)
    // 改键成功后按键仍保持选中，保留节点内容以便回显/再次调整
    toast.dismiss('keyboard')
  }

  const getKeyClass = (config: KeyConfig) => {
    const base =
      'flex items-center justify-center gap-1 bg-key font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer disabled:text-text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none'
    if (highlightedKeyCodes.value.has(config.keywords)) {
      return `${base} bg-white text-brand-blue2 hover:text-brand-blue2`
    }
    return `${base} text-black-white hover:text-text-glass-default active:bg-white active:text-brand-blue2`
  }

  const getKeyDisabled = (config: KeyConfig): boolean => {
    if (highlightedKeyCodes.value.size < MAX_KEYS) return false
    return !highlightedKeyCodes.value.has(config.keywords)
  }
</script>

<style scoped></style>
